import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuCheckboxItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { toast } from "sonner";
import { PortfolioKanban } from "../components/portfolio/PortfolioKanban";
import { InnoHubRefIcon } from "../components/shared/InnoHubRefLink";
import {
  Plus,
  Search,
  Filter,
  LayoutGrid,
  List,
  Kanban,
  Calendar,
  ChevronRight,
  Briefcase,
  Beaker,
  FolderKanban,
  MoreHorizontal,
  Loader2,
  X,
  CalendarDays,
  Leaf,
  Edit,
  Save,
  RefreshCw,
  Building2,
  Percent,
  AlertTriangle,
  Upload,
  Lightbulb,
  Users2,
} from "lucide-react";

const statusLabels = {
  idea: "Idea",
  backlog: "Backlog",
  in_progress: "En Progreso",
  pilot: "Piloto",
  scaling: "Escalando",
  completed: "Completado",
  cancelled: "Cancelado",
};

const typeLabels = {
  digital: "Digital",
  open_innovation: "Open Innovation",
  rd: "I+D",
  pilot: "Piloto",
  prop: "PROP (propuesta)",
};

/** True when an initiative has an Airtable link but the local record was modified after the last push */
const isDesynced = (init) =>
  init.airtable_id &&
  (!init.last_airtable_push ||
    (init.updated_at && init.updated_at > init.last_airtable_push));

export default function PortfolioPage() {
  const [initiatives, setInitiatives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("table");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterYear, setFilterYear] = useState("all");
  const [filterArea, setFilterArea] = useState("all");
  const [filterEsgTags, setFilterEsgTags] = useState([]);
  const [filterOptions, setFilterOptions] = useState({ years: [], areas: [], esg_tags: [] });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    type: "digital",
    status: "idea",
    hypothesis: "",
    expected_impact: "",
    priority: 3,
    trl_initial: 1,
    trl_target: 5,
    budget_estimated: "",
    esg_tags: "",
    year: new Date().getFullYear(),
    innohub_path: "",
  });
  // Quick edit modal state
  const [quickEditOpen, setQuickEditOpen] = useState(false);
  const [quickEditInit, setQuickEditInit] = useState(null);
  const [quickEditData, setQuickEditData] = useState({});
  const [quickEditLoading, setQuickEditLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null);
  const [pushingId, setPushingId] = useState(null);
  const [propuestas, setPropuestas] = useState(null);
  const [propuestasLoading, setPropuestasLoading] = useState(false);

  const { token, user } = useAuth();
  const navigate = useNavigate();

  // Fix 2026-08-27: nueva pestaña "Propuestas" — todo lo lanzado (pilotos/I+D/
  // iniciativas) sin contrato aún, para estimar carga laboral por responsable.
  useEffect(() => {
    if (view !== "propuestas" || propuestas) return;
    setPropuestasLoading(true);
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/portfolio/propuestas`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(r => r.json()).then(setPropuestas).catch(() => toast.error("Error cargando propuestas")).finally(() => setPropuestasLoading(false));
  }, [view, propuestas, token]);

  // Load sync status on mount
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/airtable/sync-status`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(r => r.json()).then(setSyncStatus).catch(() => {});
  }, [token]);

  const handleSyncFromAirtable = async () => {
    setSyncing(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/airtable/sync/from-observatorio`,
        { method: "POST", headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        toast.success(`Sync Airtable: ${data.created} creadas, ${data.updated} actualizadas`);
        fetchInitiatives();
        // Refresh sync status
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/airtable/sync-status`, {
          headers: { Authorization: `Bearer ${token}` }
        }).then(r => r.json()).then(setSyncStatus).catch(() => {});
      } else {
        toast.error("Error al sincronizar con Airtable");
      }
    } catch {
      toast.error("Error al sincronizar con Airtable");
    } finally {
      setSyncing(false);
    }
  };

  const handlePushToAirtable = async (initId) => {
    setPushingId(initId);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/airtable/push/initiative/${initId}`,
        { method: "POST", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Error");
      toast.success(`Enviado a Airtable: ${data.updated_fields?.join(", ")}`);
      fetchInitiatives(); // refresh to clear desynced indicator
    } catch (e) {
      toast.error(e.message || "Error al enviar a Airtable");
    } finally {
      setPushingId(null);
    }
  };

  const openQuickEdit = (e, init) => {
    e.stopPropagation();
    setQuickEditInit(init);
    setQuickEditData({
      priority: init.priority || 3,
      trl_initial: init.trl_initial || 1,
      trl_target: init.trl_target || 5,
      area: init.area || "",
      status: init.status || "idea",
    });
    setQuickEditOpen(true);
  };

  const handleQuickEditSave = async () => {
    if (!quickEditInit) return;
    setQuickEditLoading(true);
    try {
      await api.updateInitiative(token, quickEditInit.id, {
        ...quickEditInit,
        ...quickEditData,
      });
      toast.success("Iniciativa actualizada");
      setQuickEditOpen(false);
      fetchInitiatives();
    } catch (error) {
      toast.error("Error al actualizar");
    } finally {
      setQuickEditLoading(false);
    }
  };

  const [taskCounts, setTaskCounts] = useState({});

  const fetchInitiatives = async () => {
    try {
      const data = await api.getInitiatives(token);
      setInitiatives(data);
    } catch (error) {
      toast.error("Error al cargar iniciativas");
    } finally {
      setLoading(false);
    }
  };

  const fetchFilterOptions = async () => {
    try {
      const options = await api.getInitiativeFilters(token);
      setFilterOptions(options);
    } catch (error) {
      console.error("Error fetching filter options:", error);
    }
  };

  useEffect(() => {
    fetchInitiatives();
    fetchFilterOptions();
    api.getTaskCounts(token).then((d) => setTaskCounts(d.by_initiative || {})).catch(() => {});
  }, [token]);

  const clearAllFilters = () => {
    setFilterType("all");
    setFilterStatus("all");
    setFilterYear("all");
    setFilterArea("all");
    setFilterEsgTags([]);
    setSearchQuery("");
  };

  const hasActiveFilters = filterType !== "all" || filterStatus !== "all" || 
    filterYear !== "all" || filterArea !== "all" || filterEsgTags.length > 0 || searchQuery;

  const handleCreateInitiative = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      const payload = {
        ...formData,
        budget_estimated: formData.budget_estimated ? parseFloat(formData.budget_estimated) : null,
        esg_tags: formData.esg_tags ? formData.esg_tags.split(",").map((t) => t.trim()) : [],
        year: formData.year ? parseInt(formData.year) : null,
      };
      await api.createInitiative(token, payload);
      toast.success("Iniciativa creada exitosamente");
      setDialogOpen(false);
      setFormData({
        name: "",
        description: "",
        type: "digital",
        status: "idea",
        hypothesis: "",
        expected_impact: "",
        priority: 3,
        trl_initial: 1,
        trl_target: 5,
        budget_estimated: "",
        esg_tags: "",
        year: new Date().getFullYear(),
        innohub_path: "",
      });
      fetchInitiatives();
      fetchFilterOptions();
    } catch (error) {
      toast.error("Error al crear iniciativa");
    } finally {
      setFormLoading(false);
    }
  };

  const filteredInitiatives = initiatives.filter((init) => {
    const matchesSearch =
      init.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      init.description?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesType = filterType === "all" || init.type === filterType;
    const matchesStatus = filterStatus === "all" || init.status === filterStatus;
    const matchesYear = filterYear === "all" || init.year === parseInt(filterYear);
    const matchesArea = filterArea === "all" || init.area === filterArea;
    const matchesEsg = filterEsgTags.length === 0 || 
      filterEsgTags.some(tag => init.esg_tags?.includes(tag));
    return matchesSearch && matchesType && matchesStatus && matchesYear && matchesArea && matchesEsg;
  });

  const handleKanbanStatusChange = async (initId, newStatus) => {
    const init = initiatives.find((i) => i.id === initId);
    if (!init) return;
    setInitiatives((prev) => prev.map((i) => (i.id === initId ? { ...i, status: newStatus } : i)));
    try {
      await api.updateInitiative(token, initId, { ...init, status: newStatus });
      toast.success(`"${init.name}" movida a ${statusLabels[newStatus] || newStatus}`);
    } catch (error) {
      toast.error("Error al mover la iniciativa");
      setInitiatives((prev) => prev.map((i) => (i.id === initId ? { ...i, status: init.status } : i)));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in" data-testid="portfolio-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Portfolio de Innovación</h1>
          <p className="text-muted-foreground mt-1">
            Gestiona todas las iniciativas del ciclo de innovación
            {user?.role === "admin" && syncStatus?.last_sync && (
              <span className="ml-2 text-xs text-green-600 dark:text-green-400">
                · Último sync Airtable: {new Date(syncStatus.last_sync).toLocaleString("es-ES", { day:"2-digit", month:"short", hour:"2-digit", minute:"2-digit" })}
                {" "}({syncStatus.synced_from_airtable} registros)
              </span>
            )}
          </p>
        </div>
        <div className="flex gap-2">
          {user?.role === "admin" && (
            <Button
              variant="outline"
              onClick={handleSyncFromAirtable}
              disabled={syncing}
              data-testid="sync-airtable-btn"
              title="Importar datos desde Airtable Observatorio"
            >
              {syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              {syncing ? "Sincronizando…" : "Sync Airtable"}
            </Button>
          )}
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="create-initiative-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nueva Iniciativa
              </Button>
            </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Crear Nueva Iniciativa</DialogTitle>
              <DialogDescription>
                Define los detalles de la nueva iniciativa de innovación
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateInitiative} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2 space-y-2">
                  <Label htmlFor="name">Nombre *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Nombre de la iniciativa"
                    required
                    data-testid="initiative-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="type">Tipo</Label>
                  <Select
                    value={formData.type}
                    onValueChange={(value) => setFormData({ ...formData, type: value })}
                  >
                    <SelectTrigger data-testid="initiative-type-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="digital">Digital</SelectItem>
                      <SelectItem value="open_innovation">Open Innovation</SelectItem>
                      <SelectItem value="rd">I+D</SelectItem>
                      <SelectItem value="pilot">Piloto</SelectItem>
                      <SelectItem value="prop">PROP (propuesta)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="status">Estado</Label>
                  <Select
                    value={formData.status}
                    onValueChange={(value) => setFormData({ ...formData, status: value })}
                  >
                    <SelectTrigger data-testid="initiative-status-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(statusLabels).map(([key, label]) => (
                        <SelectItem key={key} value={key}>
                          {label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2 space-y-2">
                  <Label htmlFor="description">Descripción</Label>
                  <Textarea
                    id="description"
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Describe la iniciativa..."
                    rows={3}
                  />
                </div>
                <div className="col-span-2 space-y-2">
                  <Label htmlFor="hypothesis">Hipótesis / Solución</Label>
                  <Textarea
                    id="hypothesis"
                    value={formData.hypothesis}
                    onChange={(e) => setFormData({ ...formData, hypothesis: e.target.value })}
                    placeholder="¿Qué hipótesis queremos validar?"
                    rows={2}
                  />
                </div>
                <div className="col-span-2 space-y-2">
                  <Label htmlFor="innohub_path">Carpeta InnoHub (enlace)</Label>
                  <Input
                    id="innohub_path"
                    value={formData.innohub_path}
                    onChange={(e) => setFormData({ ...formData, innohub_path: e.target.value })}
                    placeholder="https://valorizasm.sharepoint.com/... (opcional, se puede añadir después)"
                    data-testid="initiative-innohub-path-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="priority">Prioridad (1-5)</Label>
                  <Select
                    value={formData.priority.toString()}
                    onValueChange={(value) =>
                      setFormData({ ...formData, priority: parseInt(value) })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[1, 2, 3, 4, 5].map((p) => (
                        <SelectItem key={p} value={p.toString()}>
                          {p} - {p === 1 ? "Baja" : p === 5 ? "Crítica" : "Media"}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="budget">Presupuesto estimado (€)</Label>
                  <Input
                    id="budget"
                    type="number"
                    value={formData.budget_estimated}
                    onChange={(e) =>
                      setFormData({ ...formData, budget_estimated: e.target.value })
                    }
                    placeholder="50000"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="trl_initial">TRL Inicial</Label>
                  <Select
                    value={formData.trl_initial.toString()}
                    onValueChange={(value) =>
                      setFormData({ ...formData, trl_initial: parseInt(value) })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((t) => (
                        <SelectItem key={t} value={t.toString()}>
                          TRL {t}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="trl_target">TRL Objetivo</Label>
                  <Select
                    value={formData.trl_target.toString()}
                    onValueChange={(value) =>
                      setFormData({ ...formData, trl_target: parseInt(value) })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((t) => (
                        <SelectItem key={t} value={t.toString()}>
                          TRL {t}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2 space-y-2">
                  <Label htmlFor="esg_tags">Tags ESG (separados por coma)</Label>
                  <Input
                    id="esg_tags"
                    value={formData.esg_tags}
                    onChange={(e) => setFormData({ ...formData, esg_tags: e.target.value })}
                    placeholder="CO2 reduction, circular economy, biodiversity"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" disabled={formLoading} data-testid="create-initiative-submit">
                  {formLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                  Crear Iniciativa
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {user?.role === "admin" && (() => {
        const missingInnohub = initiatives.filter((i) => i.status === "in_progress" && !i.innohub_path && (!i.refs || i.refs.length === 0));
        if (missingInnohub.length === 0) return null;
        return (
          <div
            className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/20 dark:border-amber-900/40 p-3 text-sm"
            data-testid="innohub-missing-alert"
          >
            <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <p>
              <strong>{missingInnohub.length}</strong> iniciativa{missingInnohub.length === 1 ? "" : "s"} en "En Progreso" sin Carpeta InnoHub enlazada:{" "}
              {missingInnohub.slice(0, 5).map((i) => i.name).join(", ")}{missingInnohub.length > 5 ? "…" : ""}
            </p>
          </div>
        );
      })()}

      {/* Filters and Views */}
      <div className="flex flex-col gap-4">
        <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
          <div className="flex flex-wrap gap-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Buscar iniciativas..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 w-64"
                data-testid="search-initiatives"
              />
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-40" data-testid="filter-type">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los tipos</SelectItem>
                {Object.entries(typeLabels).map(([key, label]) => (
                  <SelectItem key={key} value={key}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-40" data-testid="filter-status">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos los estados</SelectItem>
                {Object.entries(statusLabels).map(([key, label]) => (
                  <SelectItem key={key} value={key}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterYear} onValueChange={setFilterYear}>
              <SelectTrigger className="w-32" data-testid="filter-year">
                <CalendarDays className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Año" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                {filterOptions.years?.map((year) => (
                  <SelectItem key={year} value={year.toString()}>
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filterArea} onValueChange={setFilterArea}>
              <SelectTrigger className="w-44" data-testid="filter-area">
                <SelectValue placeholder="Área" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas las áreas</SelectItem>
                {filterOptions.areas?.map((area) => (
                  <SelectItem key={area} value={area}>
                    {area}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="gap-2" data-testid="filter-esg">
                  <Leaf className="w-4 h-4" />
                  ESG {filterEsgTags.length > 0 && `(${filterEsgTags.length})`}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56 max-h-72 overflow-y-auto">
                <DropdownMenuLabel>Tags ESG</DropdownMenuLabel>
                <DropdownMenuSeparator />
                {filterOptions.esg_tags?.map((tag) => (
                  <DropdownMenuCheckboxItem
                    key={tag}
                    checked={filterEsgTags.includes(tag)}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        setFilterEsgTags([...filterEsgTags, tag]);
                      } else {
                        setFilterEsgTags(filterEsgTags.filter(t => t !== tag));
                      }
                    }}
                  >
                    {tag}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <Tabs value={view} onValueChange={setView}>
            <TabsList>
              <TabsTrigger value="table" data-testid="view-table">
                <List className="w-4 h-4" />
              </TabsTrigger>
              <TabsTrigger value="kanban" data-testid="view-kanban">
                <Kanban className="w-4 h-4" />
              </TabsTrigger>
              <TabsTrigger value="grid" data-testid="view-grid">
                <LayoutGrid className="w-4 h-4" />
              </TabsTrigger>
              <TabsTrigger value="propuestas" data-testid="view-propuestas" className="gap-1.5">
                <Lightbulb className="w-4 h-4" /> Propuestas
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>
        {/* Active Filters Pills */}
        {hasActiveFilters && (
          <div className="flex flex-wrap gap-2 items-center">
            <span className="text-sm text-muted-foreground">Filtros activos:</span>
            {filterType !== "all" && (
              <Badge variant="secondary" className="gap-1">
                Tipo: {typeLabels[filterType]}
                <X className="w-3 h-3 cursor-pointer" onClick={() => setFilterType("all")} />
              </Badge>
            )}
            {filterStatus !== "all" && (
              <Badge variant="secondary" className="gap-1">
                Estado: {statusLabels[filterStatus]}
                <X className="w-3 h-3 cursor-pointer" onClick={() => setFilterStatus("all")} />
              </Badge>
            )}
            {filterYear !== "all" && (
              <Badge variant="secondary" className="gap-1">
                Año: {filterYear}
                <X className="w-3 h-3 cursor-pointer" onClick={() => setFilterYear("all")} />
              </Badge>
            )}
            {filterArea !== "all" && (
              <Badge variant="secondary" className="gap-1">
                Área: {filterArea}
                <X className="w-3 h-3 cursor-pointer" onClick={() => setFilterArea("all")} />
              </Badge>
            )}
            {filterEsgTags.map(tag => (
              <Badge key={tag} variant="secondary" className="gap-1 bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                {tag}
                <X className="w-3 h-3 cursor-pointer" onClick={() => setFilterEsgTags(filterEsgTags.filter(t => t !== tag))} />
              </Badge>
            ))}
            <Button variant="ghost" size="sm" onClick={clearAllFilters} className="text-xs">
              Limpiar todos
            </Button>
          </div>
        )}
      </div>

      {/* Content Views */}
      {view === "table" && (
        <Card>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nombre</TableHead>
                <TableHead>Año</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Prioridad</TableHead>
                <TableHead>TRL</TableHead>
                <TableHead>Tags ESG</TableHead>
                <TableHead className="w-10"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredInitiatives.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    No se encontraron iniciativas
                  </TableCell>
                </TableRow>
              ) : (
                filteredInitiatives.map((init) => (
                  <TableRow
                    key={init.id}
                    className={`cursor-pointer hover:bg-muted/50 ${isDesynced(init) ? "bg-amber-50/40 dark:bg-amber-900/10" : ""}`}
                    onClick={() => navigate(`/initiative/${init.id}`)}
                    data-testid={`initiative-row-${init.id}`}
                  >
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                          {init.type === "pilot" ? (
                            <Beaker className="w-4 h-4 text-primary" />
                          ) : init.type === "rd" ? (
                            <FolderKanban className="w-4 h-4 text-primary" />
                          ) : (
                            <Briefcase className="w-4 h-4 text-primary" />
                          )}
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5">
                            <p className="font-medium truncate" title={init.name}>{init.name}</p>
                            <InnoHubRefIcon refs={init.refs} innohubPath={init.innohub_path} />
                            {isDesynced(init) && (
                              <AlertTriangle
                                className="w-3.5 h-3.5 text-amber-500 flex-shrink-0"
                                title="Cambios pendientes de sincronizar con el catálogo de origen"
                                data-testid={`desynced-icon-${init.id}`}
                              />
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground truncate max-w-[200px]" title={init.description}>
                            {init.description}
                          </p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {init.year && (
                        <Badge variant="outline" className="font-mono">
                          {init.year}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{typeLabels[init.type] || init.type}</Badge>
                    </TableCell>
                    <TableCell>
                      <span className={`status-badge status-${init.status?.replace("_", "-")}`}>
                        {statusLabels[init.status] || init.status}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div
                        className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white priority-${init.priority}`}
                      >
                        {init.priority}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="font-mono text-sm inline-flex items-center gap-1">
                        {init.trl_initial === 4 && init.trl_target === 7 && !init.trl_verified ? (
                          <span className="text-muted-foreground italic" title="Valor por defecto de la migración, nunca evaluado — no es un TRL real" data-testid={`trl-unverified-${init.id}`}>
                            Sin TRL
                          </span>
                        ) : (
                          <>{init.trl_initial} → {init.trl_target}</>
                        )}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1 flex-wrap max-w-[150px]">
                        {init.esg_tags?.slice(0, 2).map((tag, i) => (
                          <Badge key={i} variant="secondary" className="text-xs">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      {init.airtable_id ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          className={`h-7 w-7 p-0 ${isDesynced(init) ? "text-amber-500 hover:text-amber-700 hover:bg-amber-50" : "text-emerald-600 hover:text-emerald-800 hover:bg-emerald-50"}`}
                          disabled={pushingId === init.id}
                          onClick={() => handlePushToAirtable(init.id)}
                          title={isDesynced(init) ? "Sincronizar cambios con Airtable" : "Forzar push a Airtable"}
                          data-testid={`push-btn-${init.id}`}
                        >
                          {pushingId === init.id ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <Upload className="w-3.5 h-3.5" />
                          )}
                        </Button>
                      ) : (
                        <ChevronRight className="w-4 h-4 text-muted-foreground" />
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Card>
      )}

      {view === "kanban" && (
        <PortfolioKanban
          initiatives={filteredInitiatives}
          onStatusChange={handleKanbanStatusChange}
          onOpen={(id) => navigate(`/initiative/${id}`)}
          onQuickEdit={openQuickEdit}
          isDesynced={isDesynced}
          taskCounts={taskCounts}
        />
      )}

      {view === "grid" && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredInitiatives.map((init) => (
            <Card
              key={init.id}
              className="cursor-pointer card-hover"
              onClick={() => navigate(`/initiative/${init.id}`)}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    {init.type === "pilot" ? (
                      <Beaker className="w-5 h-5 text-primary" />
                    ) : init.type === "rd" ? (
                      <FolderKanban className="w-5 h-5 text-primary" />
                    ) : (
                      <Briefcase className="w-5 h-5 text-primary" />
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {isDesynced(init) && (
                      <AlertTriangle className="w-3.5 h-3.5 text-amber-500" title="Sin sincronizar con Airtable" />
                    )}
                    {init.year && (
                      <Badge variant="secondary" className="font-mono text-xs">
                        {init.year}
                      </Badge>
                    )}
                    <span className={`status-badge status-${init.status?.replace("_", "-")}`}>
                      {statusLabels[init.status]}
                    </span>
                  </div>
                </div>
                <CardTitle className="text-lg mt-3">{init.name}</CardTitle>
                <CardDescription className="line-clamp-2">{init.description}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between">
                  <div className="flex gap-1">
                    <Badge variant="outline">{typeLabels[init.type]}</Badge>
                  </div>
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    {init.trl_initial === 4 && init.trl_target === 7 && !init.trl_verified ? (
                      <span className="italic" title="Valor por defecto de la migración, nunca evaluado" data-testid={`trl-unverified-card-${init.id}`}>Sin TRL</span>
                    ) : (
                      <>
                        <span>TRL {init.trl_initial}</span>
                        <ChevronRight className="w-4 h-4" />
                        <span>TRL {init.trl_target}</span>
                      </>
                    )}
                  </div>
                </div>
                {init.esg_tags?.length > 0 && (
                  <div className="flex gap-1 mt-3 flex-wrap">
                    {init.esg_tags.slice(0, 3).map((tag, i) => (
                      <Badge key={i} variant="secondary" className="text-xs">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {view === "propuestas" && (
        <div className="space-y-4" data-testid="propuestas-panel">
          <Card className="bg-amber-50/50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-900/40">
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground">
                Todo lo que está <b>lanzado pero sin contrato/encargo formal aún</b> (iniciativas en Idea/Evaluación + proyectos PG-07 en Definición/Propuesta) — pista rápida de posible carga laboral antes de comprometerse.
              </p>
            </CardContent>
          </Card>

          {propuestasLoading ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : !propuestas || propuestas.total === 0 ? (
            <Card>
              <CardContent className="py-16 text-center text-muted-foreground" data-testid="propuestas-empty">
                No hay propuestas sin contrato pendientes ahora mismo.
              </CardContent>
            </Card>
          ) : (
            <>
              <Card data-testid="propuestas-workload-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Users2 className="w-4 h-4" /> Carga laboral por responsable ({propuestas.total} propuestas)
                  </CardTitle>
                </CardHeader>
                <CardContent className="flex flex-wrap gap-2">
                  {[...propuestas.workload_by_owner].sort((a, b) => (a.owner === "Sin responsable" ? 1 : b.owner === "Sin responsable" ? -1 : 0)).map((w) => (
                    <Badge
                      key={w.owner}
                      variant={w.owner === "Sin responsable" ? "outline" : "secondary"}
                      className={`text-xs ${w.owner === "Sin responsable" ? "text-muted-foreground border-dashed" : ""}`}
                      data-testid={`propuestas-workload-${w.owner.replace(/\s+/g, "-")}`}
                    >
                      {w.owner} · {w.count}
                    </Badge>
                  ))}
                </CardContent>
              </Card>

              <Card>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nombre</TableHead>
                      <TableHead>Origen</TableHead>
                      <TableHead>Fase</TableHead>
                      <TableHead>Área</TableHead>
                      <TableHead>Responsable</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {propuestas.items.map((it) => (
                      <TableRow
                        key={`${it.source}-${it.id}`}
                        className={it.source === "initiative" ? "cursor-pointer hover:bg-muted/50" : ""}
                        onClick={() => it.source === "initiative" && navigate(`/initiative/${it.id}`)}
                        data-testid={`propuesta-row-${it.id}`}
                      >
                        <TableCell className="font-medium">{it.name}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-xs">
                            {it.source === "pg07" ? "PG-07" : "Iniciativa"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm">{it.source === "pg07" ? it.stage : (statusLabels[it.stage] || it.stage)}</TableCell>
                        <TableCell className="text-sm text-muted-foreground">{it.area || "—"}</TableCell>
                        <TableCell className="text-sm">{it.owner}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            </>
          )}
        </div>
      )}

      {/* Quick Edit Modal */}
      <Dialog open={quickEditOpen} onOpenChange={setQuickEditOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edición Rápida: {quickEditInit?.name}</DialogTitle>
            <DialogDescription>Modifica TRL, prioridad y área</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Prioridad</Label>
                <Select 
                  value={String(quickEditData.priority || 3)} 
                  onValueChange={(v) => setQuickEditData({ ...quickEditData, priority: parseInt(v) })}
                >
                  <SelectTrigger data-testid="quick-edit-priority">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 - Baja</SelectItem>
                    <SelectItem value="2">2</SelectItem>
                    <SelectItem value="3">3 - Media</SelectItem>
                    <SelectItem value="4">4</SelectItem>
                    <SelectItem value="5">5 - Crítica</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Estado</Label>
                <Select 
                  value={quickEditData.status || "idea"} 
                  onValueChange={(v) => setQuickEditData({ ...quickEditData, status: v })}
                >
                  <SelectTrigger data-testid="quick-edit-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="idea">Idea</SelectItem>
                    <SelectItem value="backlog">Backlog</SelectItem>
                    <SelectItem value="in_progress">En Progreso</SelectItem>
                    <SelectItem value="pilot">Piloto</SelectItem>
                    <SelectItem value="scaling">Escalando</SelectItem>
                    <SelectItem value="completed">Completado</SelectItem>
                    <SelectItem value="cancelled">Cancelado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>TRL Inicial</Label>
                <Select 
                  value={String(quickEditData.trl_initial || 1)} 
                  onValueChange={(v) => setQuickEditData({ ...quickEditData, trl_initial: parseInt(v), trl_verified: true })}
                >
                  <SelectTrigger data-testid="quick-edit-trl-initial">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1,2,3,4,5,6,7,8,9].map(t => (
                      <SelectItem key={t} value={String(t)}>TRL {t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>TRL Objetivo</Label>
                <Select 
                  value={String(quickEditData.trl_target || 5)} 
                  onValueChange={(v) => setQuickEditData({ ...quickEditData, trl_target: parseInt(v), trl_verified: true })}
                >
                  <SelectTrigger data-testid="quick-edit-trl-target">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[1,2,3,4,5,6,7,8,9].map(t => (
                      <SelectItem key={t} value={String(t)}>TRL {t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Área</Label>
              <Input
                value={quickEditData.area || ""}
                onChange={(e) => setQuickEditData({ ...quickEditData, area: e.target.value })}
                placeholder="Ej: Tratamiento Residuos, Smart City..."
                data-testid="quick-edit-area"
              />
            </div>
          </div>
          <DialogFooter>
            <div className="mr-auto flex flex-col gap-1">
              {quickEditInit?.airtable_id && (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={pushingId === quickEditInit?.id}
                  data-testid="push-airtable-btn"
                  onClick={() => handlePushToAirtable(quickEditInit.id)}
                  className="text-xs text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                  title="Enviar estado y seguimiento a Airtable"
                >
                  {pushingId === quickEditInit?.id ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5 mr-1" />}
                  Push Airtable (manual)
                </Button>
              )}
              {quickEditInit?.last_airtable_push && (
                <p className="text-[10px] text-emerald-700/80 pl-1" data-testid="last-airtable-push">
                  ✓ Último auto-sync: {new Date(quickEditInit.last_airtable_push).toLocaleString("es-ES", {
                    day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit"
                  })}
                </p>
              )}
              {quickEditInit?.airtable_id && !quickEditInit?.last_airtable_push && (
                <p className="text-[10px] text-muted-foreground pl-1">
                  Los cambios se envían a Airtable automáticamente al guardar
                </p>
              )}
            </div>
            <Button variant="outline" onClick={() => setQuickEditOpen(false)}>
              <X className="w-4 h-4 mr-2" />
              Cancelar
            </Button>
            <Button onClick={handleQuickEditSave} disabled={quickEditLoading} data-testid="quick-edit-save">
              {quickEditLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
